import { Box, Button, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import SCurve from '../components/SCurve';
import { Section, SectionHeading, Reveal, Eyebrow } from '../components/ui';
import { EASE, RADIUS, TYPE, hoverLift } from '../theme/tokens';

// Cele patru direcții, în ordinea în care apar și pe pagina de servicii.
const CAPABILITIES = [
  { key: 'contract', icon: <TimelineRoundedIcon /> },
  { key: 'quality', icon: <ChecklistRoundedIcon /> },
  { key: 'archaeology', icon: <HistoryEduRoundedIcon /> },
  { key: 'lowCurrent', icon: <SensorsRoundedIcon /> },
];

// Decorul hero-ului: două lumini și un caroiaj de planșă, amândouă statice.
// Un gradient animat la infinit repictează tot ecranul la fiecare cadru și
// ține radioul GPU pornit degeaba pe telefon — costul se vede în baterie.
const heroDecor = {
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    backgroundImage: `radial-gradient(1100px 620px at 15% -18%, var(--qf-hero-1), transparent 62%),
                      radial-gradient(820px 520px at 92% 8%, var(--qf-hero-2), transparent 64%)`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    pointerEvents: 'none',
    backgroundImage: `linear-gradient(var(--qf-hero-grid) 1px, transparent 1px),
                      linear-gradient(90deg, var(--qf-hero-grid) 1px, transparent 1px)`,
    backgroundSize: '64px 64px',
    // Caroiajul se stinge spre margini, ca să nu pară un tabel.
    maskImage: 'radial-gradient(900px 560px at 50% 0%, #000, transparent 76%)',
    WebkitMaskImage: 'radial-gradient(900px 560px at 50% 0%, #000, transparent 76%)',
  },
};

export default function Home() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  // Intrarea hero-ului: fiecare rând urcă puțin, la 90ms distanță unul de
  // altul. Fără listă de întârzieri scrise de mână în cinci locuri.
  const rise = (delay) =>
    reduce
      ? {}
      : {
          component: motion.div,
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: EASE },
        };

  return (
    <>
      {/* ─────────────────────────────  Hero  ───────────────────────────── */}
      <Section first component="header" sx={heroDecor}>
        <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
          <Box {...rise(0)}>
            <Eyebrow>{t('home.eyebrow')}</Eyebrow>
          </Box>

          <Box {...rise(0.08)}>
            <Typography component="h1" sx={{ ...TYPE.display, mt: 2.5, mb: 0, color: 'var(--qf-ink)' }}>
              {t('home.title')}
            </Typography>
          </Box>

          <Box {...rise(0.16)}>
            <Typography
              component="p"
              sx={{
                ...TYPE.lede,
                mt: 3,
                mb: 0,
                mx: 'auto',
                maxWidth: '58ch',
                color: 'var(--qf-ink-2)',
                textWrap: 'pretty',
              }}
            >
              {t('home.description')}
            </Typography>
          </Box>

          <Box
            {...rise(0.24)}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              gap: 1.5,
              mt: { xs: 4.5, md: 5.5 },
            }}
          >
            <Button component={Link} to="/services" variant="contained" size="large">
              {t('home.ctaPrimary')}
            </Button>
            <Button component={Link} to="/about" variant="outlined" size="large">
              {t('home.ctaSecondary')}
            </Button>
          </Box>
        </Box>

        {/* Planșa cu curba S. Stă sub text, pe toată lățimea coloanei: pe
            telefon un grafic înghesuit lângă text nu se citește. */}
        <Box {...rise(0.34)} sx={{ mt: { xs: 6, md: 9 }, maxWidth: 900, mx: 'auto' }}>
          <SCurve t={t} />
        </Box>
      </Section>

      {/* ──────────────────────────  Ce facem  ──────────────────────────── */}
      <Section tone="alt">
        <SectionHeading
          align="center"
          eyebrow={t('home.capabilitiesEyebrow')}
          title={t('home.capabilitiesTitle')}
          lede={t('home.capabilitiesLede')}
          sx={{ mb: { xs: 5, md: 7 } }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {CAPABILITIES.map((cap, i) => (
            <Reveal key={cap.key} delay={i * 0.07}>
              <Box
                component={Link}
                to="/services"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  p: { xs: 2.5, md: 3 },
                  borderRadius: `${RADIUS.xl}px`,
                  backgroundColor: 'var(--qf-card)',
                  border: '1px solid var(--qf-line)',
                  boxShadow: 'var(--qf-shadow-sm)',
                  textDecoration: 'none',
                  color: 'var(--qf-ink)',
                  ...hoverLift,
                  '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 3 },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 46,
                    height: 46,
                    mb: 2.5,
                    borderRadius: '14px',
                    backgroundColor: 'var(--qf-accent-soft)',
                    color: 'var(--qf-accent)',
                    '& svg': { fontSize: 24 },
                  }}
                >
                  {cap.icon}
                </Box>

                <Typography component="h3" sx={{ ...TYPE.h3, mb: 1.25, textWrap: 'balance' }}>
                  {t(`services.${cap.key}.title`)}
                </Typography>

                <Typography sx={{ ...TYPE.small, color: 'var(--qf-ink-2)', textWrap: 'pretty' }}>
                  {t(`services.${cap.key}.description`)}
                </Typography>

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 'auto',
                    pt: 2.5,
                    ...TYPE.small,
                    fontWeight: 600,
                    color: 'var(--qf-accent)',
                  }}
                >
                  {t('home.capabilitiesLink')}
                  <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Section>

      {/* ─────────────────────────  Invitația  ──────────────────────────── */}
      <Section tone="deep">
        <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
          <SectionHeading
            align="center"
            onDark
            eyebrow={t('home.ctaEyebrow')}
            title={t('home.ctaTitle')}
            lede={t('home.ctaBody')}
          />

          <Reveal
            delay={0.1}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              gap: 1.5,
              mt: { xs: 4, md: 5 },
            }}
          >
            <Button component={Link} to="/contact" variant="onDarkFilled" size="large">
              {t('home.ctaAction')}
            </Button>
            <Button component={Link} to="/services" variant="onDark" size="large">
              {t('home.ctaPrimary')}
            </Button>
          </Reveal>
        </Box>
      </Section>
    </>
  );
}
